import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  ExternalLink,
  GraduationCap,
  LoaderCircle,
  Mail,
  Megaphone,
  Pencil,
  Plus,
  Search,
  Star,
  TrendingUp,
  UserCheck,
  UserRound,
  UserX,
  Users,
  WalletCards,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './InstructorDashboard.css';

const InstructorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'sub-admin';
  const [analytics, setAnalytics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [courseAnalytics, setCourseAnalytics] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [totalInstructors, setTotalInstructors] = useState(0);
  const [activeInstructors, setActiveInstructors] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('courses');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [timeRange, setTimeRange] = useState('30');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (isAdmin) {
          const response = await api.get('/admin/users?role=instructor&limit=100');
          const list = response.data.users || [];
          setInstructors(list);
          setTotalInstructors(parseInt(response.data.total || list.length, 10));
          setActiveInstructors(list.filter((inst) => inst.is_active).length);
        } else {
          const [analyticsRes, summaryRes] = await Promise.all([
            api.get('/analytics/instructor'),
            api.get('/analytics/instructor/summary'),
          ]);
          setAnalytics(analyticsRes.data);
          setSummary(summaryRes.data);
        }
      } catch (error) {
        console.error('Error fetching instructor data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin]);

  const fetchStudents = useCallback(async () => {
    try {
      const courses = analytics?.course_performance || [];
      if (courses.length === 0) return;

      const courseId = selectedCourse || courses[0]?.id;
      if (!courseId) return;

      const response = await api.get(`/admin/reports/student-performance?course_id=${courseId}`);
      setStudents(response.data.report || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  }, [analytics, selectedCourse]);

  const fetchCourseAnalytics = useCallback(async () => {
    try {
      const courses = analytics?.course_performance || [];
      if (courses.length === 0) return;

      const courseId = selectedCourse || courses[0]?.id;
      if (!courseId) return;

      const response = await api.get(`/analytics/course/${courseId}`);
      setCourseAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching course analytics:', error);
    }
  }, [analytics, selectedCourse]);

  useEffect(() => {
    if (activeTab === 'students' && analytics) {
      fetchStudents();
    }
    if (activeTab === 'analytics' && analytics) {
      fetchCourseAnalytics();
    }
  }, [activeTab, analytics, fetchStudents, fetchCourseAnalytics]);

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      await api.put(`/admin/users/${id}`, { is_active: !currentStatus });
      setInstructors((prev) =>
        prev.map((inst) =>
          inst.id === id ? { ...inst, is_active: !currentStatus } : inst
        )
      );
    } catch (error) {
      console.error('Error updating instructor:', error);
      alert('Unable to update instructor status.');
    }
  };

  const toggleAccordion = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const filteredInstructors = instructors.filter((instructor) => {
    const term = searchTerm.toLowerCase();
    return (
      instructor.first_name?.toLowerCase().includes(term) ||
      instructor.last_name?.toLowerCase().includes(term) ||
      instructor.email?.toLowerCase().includes(term)
    );
  });

  const filteredCourses = (analytics?.course_performance || []).filter((course) => {
    const term = searchTerm.toLowerCase();
    return course.title?.toLowerCase().includes(term);
  });

  const renderInstructorAccordion = () => (
    <div className="course-accordion-container">
      {filteredInstructors.length === 0 ? (
        <div className="no-data">
          <UserRound size={24} aria-hidden="true" />
          <strong>No instructors found</strong>
          <span>Try a different name or email address.</span>
        </div>
      ) : (
        <div className="accordion-list">
          {filteredInstructors.map((instructor) => (
            <div
              key={instructor.id}
              className={`accordion-item ${expandedId === instructor.id ? 'expanded' : ''}`}
            >
              <div className="accordion-header" onClick={() => toggleAccordion(instructor.id)}>
                <div className="header-info">
                  <div className="title-group">
                    <span className="course-title">{instructor.first_name} {instructor.last_name}</span>
                    <span className="course-category">ID: {instructor.id}</span>
                  </div>
                  <div className="meta-group">
                    <span className="instructor-badge">
                      <Mail size={14} aria-hidden="true" />
                      {instructor.email}
                    </span>
                    <span className={`status-pill ${instructor.is_active ? 'active' : 'inactive'}`}>
                      {instructor.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="learner-count">
                      <CalendarDays size={14} aria-hidden="true" />
                      Joined {new Date(instructor.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <span className="btn-icon-toggle" aria-hidden="true">
                    <ChevronDown size={18} />
                  </span>
                </div>
              </div>

              {expandedId === instructor.id && (
                <div className="accordion-content">
                  <div className="content-grid">
                    <div className="description-section">
                      <h5>Instructor Profile</h5>
                      <p>Full Name: {instructor.first_name} {instructor.last_name}</p>
                      <p>Email: {instructor.email}</p>
                      <p>Phone: {instructor.phone || 'Not provided'}</p>
                      <p>Bio: {instructor.bio || 'No biography provided.'}</p>
                    </div>
                    <div className="stats-section">
                      <div className="stat-item">
                        <span className="label">Joined</span>
                        <strong>{new Date(instructor.created_at).toLocaleDateString()}</strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">Last Login</span>
                        <strong>
                          {instructor.last_login
                            ? new Date(instructor.last_login).toLocaleDateString()
                            : 'Never'}
                        </strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">Role</span>
                        <strong>Instructor</strong>
                      </div>
                    </div>
                  </div>
                  <div className="content-actions">
                    <button
                      className={instructor.is_active ? 'btn-warning' : 'btn-success'}
                      onClick={() => handleStatusToggle(instructor.id, instructor.is_active)}
                    >
                      {instructor.is_active
                        ? <UserX size={16} aria-hidden="true" />
                        : <UserCheck size={16} aria-hidden="true" />}
                      {instructor.is_active ? 'Deactivate Instructor' : 'Activate Instructor'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCourseAccordion = () => (
    <div className="course-accordion-container">
      {filteredCourses.length === 0 ? (
        <div className="no-data">
          <BookOpen size={24} aria-hidden="true" />
          <strong>No courses found</strong>
          <span>{searchTerm ? 'Try a different course title.' : 'Create a course to start tracking performance.'}</span>
        </div>
      ) : (
        <div className="accordion-list">
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className={`accordion-item ${expandedId === course.id ? 'expanded' : ''}`}
            >
              <div className="accordion-header" onClick={() => toggleAccordion(course.id)}>
                <div className="header-info">
                  <div className="title-group">
                    <span className="course-title">{course.title}</span>
                    <span className="course-category">{course.category_name || 'General'}</span>
                  </div>
                  <div className="meta-group">
                    <span className="metric-chip">
                      <Users size={14} aria-hidden="true" />
                      {course.enrollments} Learners
                    </span>
                    <span className="metric-chip">
                      <CheckCircle2 size={14} aria-hidden="true" />
                      {course.completions} completions
                    </span>
                    <span className="course-revenue">
                      <CircleDollarSign size={14} aria-hidden="true" />
                      ${parseFloat(course.revenue || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <span className="btn-icon-toggle" aria-hidden="true">
                    <ChevronDown size={18} />
                  </span>
                </div>
              </div>

              {expandedId === course.id && (
                <div className="accordion-content">
                  <div className="content-grid">
                    <div className="description-section">
                      <h5>Performance Overview</h5>
                      <p>Avg. Rating: {course.avg_rating > 0 ? `${Number(course.avg_rating).toFixed(1)} / 5.0` : 'No reviews yet'}</p>
                      <p>Total Revenue: ${parseFloat(course.revenue || 0).toFixed(2)}</p>
                      <p>Completion Rate: {course.enrollments > 0 ? Math.round((course.completions / course.enrollments) * 100) : 0}%</p>
                    </div>
                    <div className="stats-section">
                      <div className="stat-item">
                        <span className="label">Enrollments</span>
                        <strong>{course.enrollments}</strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">Completions</span>
                        <strong>{course.completions}</strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">Revenue</span>
                        <strong>${parseFloat(course.revenue || 0).toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="content-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate(`/courses/${course.id}`)}
                    >
                      <ExternalLink size={16} aria-hidden="true" />
                      View Page
                    </button>
                    <button
                      type="button"
                      className="btn-edit"
                      onClick={() => navigate(`/edit-course/${course.id}`)}
                    >
                      <Pencil size={16} aria-hidden="true" />
                      Full Editor
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate(`/courses/${course.id}/manage-announcements`)}
                    >
                      <Megaphone size={16} aria-hidden="true" />
                      Announcements
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setSelectedCourse(course.id);
                        setActiveTab('analytics');
                      }}
                    >
                      <BarChart3 size={16} aria-hidden="true" />
                      Analytics
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <main className="instructor-dashboard instructor-loading" aria-busy="true">
        <LoaderCircle size={24} aria-hidden="true" />
        <span>Loading workspace</span>
      </main>
    );
  }

  if (isAdmin) {
    return (
      <div className="page instructor-dashboard admin-view">
        <div className="container">
          <section className="instructor-hero admin">
            <div className="hero-content">
              <p className="eyebrow">
                <GraduationCap size={15} aria-hidden="true" />
                Faculty administration
              </p>
              <h1>Manage instructors</h1>
              <p>Review instructor activity, manage credentials, and toggle platform access.</p>
            </div>
            <div className="hero-stats">
              <div className="hero-stat-card green">
                <span className="stat-icon"><Users size={18} aria-hidden="true" /></span>
                <div>
                  <span className="stat-label">Total instructors</span>
                  <p className="stat-value">{totalInstructors}</p>
                  <span className="stat-hint">Faculty accounts</span>
                </div>
              </div>
              <div className="hero-stat-card teal">
                <span className="stat-icon"><UserCheck size={18} aria-hidden="true" /></span>
                <div>
                  <span className="stat-label">Active</span>
                  <p className="stat-value">{activeInstructors}</p>
                  <span className="stat-hint">Access enabled</span>
                </div>
              </div>
            </div>
          </section>

          <div className="course-management-section">
            <div className="section-header-row">
              <div className="panel-header-simple">
                <p className="label">Management</p>
                <h2>Directory</h2>
              </div>
              <div className="search-bar-container">
                <Search size={17} aria-hidden="true" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            {renderInstructorAccordion()}
          </div>
        </div>
      </div>
    );
  }

  const heroStats = [
    {
      label: 'Courses',
      value: summary?.courses?.total_courses || analytics?.stats?.total_courses || 0,
      hint: 'In your library',
      icon: BookOpen,
      tone: 'green',
    },
    {
      label: 'Students',
      value: summary?.students?.total_students || analytics?.stats?.total_enrollments || 0,
      hint: 'Unique learners',
      icon: Users,
      tone: 'teal',
    },
    {
      label: 'Completions',
      value: summary?.students?.course_completions || analytics?.stats?.completions || 0,
      hint: 'Courses finished',
      icon: CheckCircle2,
      tone: 'blue',
    },
    {
      label: 'Revenue',
      value: `$${Number(summary?.revenue?.total_revenue || analytics?.stats?.total_revenue || 0).toFixed(2)}`,
      hint: 'Lifetime earnings',
      icon: CircleDollarSign,
      tone: 'amber',
    },
  ];

  const tabs = [
    { id: 'courses', label: 'My Courses', icon: BookOpen },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'revenue', label: 'Revenue', icon: WalletCards },
  ];

  return (
    <div className="page instructor-dashboard">
      <div className="container">
        <section className="instructor-hero">
          <div className="hero-content">
            <p className="eyebrow">
              <GraduationCap size={15} aria-hidden="true" />
              Instructor workspace
            </p>
            <h1>Welcome back, {user?.first_name}!</h1>
            <p>Track course performance, enrollments, and revenue in one place.</p>
          </div>
          <Link to="/create-course" className="btn-primary hero-action">
            <Plus size={17} aria-hidden="true" />
            Create course
          </Link>
          <div className="hero-stats">
            {heroStats.map(({ label, value, hint, icon: Icon, tone }) => (
              <div key={label} className={`hero-stat-card ${tone}`}>
                <span className="stat-icon"><Icon size={18} aria-hidden="true" /></span>
                <div>
                  <span className="stat-label">{label}</span>
                  <p className="stat-value">{value}</p>
                  <span className="stat-hint">{hint}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="instructor-tabs" role="tablist" aria-label="Instructor dashboard sections">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
              className={activeTab === id ? 'active' : ''}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={16} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'courses' && (
          <div className="instructor-grid">
            <div className="course-management-section">
              <div className="section-header-row">
                <div className="panel-header-simple">
                  <p className="label">Courses</p>
                  <h2>Course performance</h2>
                </div>
                <div className="search-bar-container">
                  <Search size={17} aria-hidden="true" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search courses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Link to="/create-course" className="btn-primary btn-small-cta">
                  <Plus size={16} aria-hidden="true" />
                  New course
                </Link>
              </div>
              {renderCourseAccordion()}
            </div>

            <aside className="dashboard-section sidebar">
              <div className="sidebar-heading">
                <div>
                  <p className="label">Activity</p>
                  <h2>Recent enrollments</h2>
                </div>
                <span className="sidebar-count">
                  {(summary?.recent_activity || analytics?.recent_enrollments || []).length}
                </span>
              </div>
              {(summary?.recent_activity || analytics?.recent_enrollments || []).length === 0 ? (
                <div className="no-data compact">
                  <Users size={22} aria-hidden="true" />
                  <strong>No enrollments yet</strong>
                  <span>New learner activity will appear here.</span>
                </div>
              ) : (
                <div className="recent-enrollments-list">
                  {(summary?.recent_activity || analytics?.recent_enrollments || []).slice(0, 5).map((enrollment, idx) => (
                    <div key={enrollment.id || idx} className="enrollment-item">
                      <span className="student-avatar" aria-hidden="true">
                        {(enrollment.user_name || enrollment.first_name || 'L').charAt(0).toUpperCase()}
                      </span>
                      <div className="student-info">
                        <span className="student-name">
                          {enrollment.user_name || `${enrollment.first_name} ${enrollment.last_name}`}
                        </span>
                        <span className="student-email">{enrollment.email}</span>
                        <span className="course-name">{enrollment.course_title}</span>
                      </div>
                      <div className="enrollment-info">
                        <span className="enrollment-date">
                          {new Date(enrollment.created_at || enrollment.enrolled_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-section">
            <div className="section-header-row analytics-controls">
              <div className="panel-header-simple">
                <p className="label">Insights</p>
                <h2>Course analytics</h2>
              </div>
              <select
                aria-label="Select course for analytics"
                value={selectedCourse || ''}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                }}
              >
                <option value="">Select a course</option>
                {(analytics?.course_performance || []).map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>

            {courseAnalytics ? (
              <div className="analytics-grid">
                <div className="analytics-card">
                  <div className="card-heading">
                    <span className="card-heading-icon green"><TrendingUp size={17} aria-hidden="true" /></span>
                    <h3>Enrollment stats</h3>
                  </div>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-value">{courseAnalytics.enrollments?.total || 0}</span>
                      <span className="stat-label">Total</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{courseAnalytics.enrollments?.completed || 0}</span>
                      <span className="stat-label">Completed</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{Math.round(courseAnalytics.enrollments?.avg_progress || 0)}%</span>
                      <span className="stat-label">Avg Progress</span>
                    </div>
                  </div>
                </div>

                <div className="analytics-card">
                  <div className="card-heading">
                    <span className="card-heading-icon amber"><Star size={17} aria-hidden="true" /></span>
                    <h3>Reviews</h3>
                  </div>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-value">{Number(courseAnalytics.reviews?.avg_rating || 0).toFixed(1)}</span>
                      <span className="stat-label">Avg Rating</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{courseAnalytics.reviews?.total_reviews || 0}</span>
                      <span className="stat-label">Reviews</span>
                    </div>
                  </div>
                </div>

                <div className="analytics-card full-width">
                  <div className="card-heading">
                    <span className="card-heading-icon blue"><BookOpen size={17} aria-hidden="true" /></span>
                    <h3>Lesson completion</h3>
                  </div>
                  <div className="lesson-stats-list">
                    {(courseAnalytics.lesson_stats || []).map((lesson) => (
                      <div key={lesson.id} className="lesson-stat-item">
                        <span className="lesson-title">{lesson.title}</span>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${lesson.total_enrolled > 0 ? (lesson.completions / lesson.total_enrolled) * 100 : 0}%`
                            }}
                          />
                        </div>
                        <span className="completion-count">
                          {lesson.completions}/{lesson.total_enrolled}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analytics-card full-width">
                  <div className="card-heading">
                    <span className="card-heading-icon teal"><BarChart3 size={17} aria-hidden="true" /></span>
                    <h3>Quiz performance</h3>
                  </div>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Quiz</th>
                          <th>Attempts</th>
                          <th>Avg Score</th>
                          <th>Pass Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(courseAnalytics.quiz_performance || []).map((quiz) => (
                          <tr key={quiz.id}>
                            <td>{quiz.title}</td>
                            <td>{quiz.attempts}</td>
                            <td>{Math.round(quiz.avg_score || 0)}%</td>
                            <td>{quiz.attempts > 0 ? Math.round((quiz.passed / quiz.attempts) * 100) : 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-data no-data-panel">
                <BarChart3 size={26} aria-hidden="true" />
                <strong>Select a course</strong>
                <span>Its enrollment, review, lesson, and quiz metrics will appear here.</span>
              </div>
            )}
          </div>
        )}

        {activeTab === 'students' && (
          <div className="students-section">
            <div className="section-header-row">
              <div className="panel-header-simple">
                <p className="label">Students</p>
                <h2>Performance Overview</h2>
              </div>
              <select
                value={selectedCourse || ''}
                onChange={(e) => setSelectedCourse(e.target.value)}
              >
                <option value="">All Courses</option>
                {(analytics?.course_performance || []).map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="dashboard-section">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Progress</th>
                      <th>Quiz Avg</th>
                      <th>Lessons</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.length === 0 ? (
                      <tr>
                        <td colSpan="6">
                          <div className="no-data compact">
                            <Users size={22} aria-hidden="true" />
                            <strong>No students found</strong>
                            <span>Student performance will appear after enrollment.</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      students.map((student, idx) => (
                        <tr key={`${student.user_id}-${idx}`}>
                          <td>
                            <div>
                              <strong>{student.first_name} {student.last_name}</strong>
                              <br />
                              <small>{student.email}</small>
                            </div>
                          </td>
                          <td>{student.course_title}</td>
                          <td>
                            <div className="progress-cell">
                              <div className="progress-bar small">
                                <div
                                  className="progress-fill"
                                  style={{ width: `${student.progress_percentage || 0}%` }}
                                />
                              </div>
                              <span>{Math.round(student.progress_percentage || 0)}%</span>
                            </div>
                          </td>
                          <td>{Math.round(student.avg_quiz_score || 0)}%</td>
                          <td>{student.lessons_completed}/{student.total_lessons}</td>
                          <td>
                            <span className={`status-badge ${student.completed_at ? 'completed' : 'in-progress'}`}>
                              {student.completed_at ? 'Completed' : 'In Progress'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="revenue-section">
            <div className="section-header-row">
              <div className="panel-header-simple">
                <p className="label">Earnings</p>
                <h2>Revenue overview</h2>
              </div>
              <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>

            <div className="revenue-grid">
              <div className="revenue-card highlight">
                <span className="revenue-icon"><CircleDollarSign size={19} aria-hidden="true" /></span>
                <h3>Total Revenue</h3>
                <p className="revenue-value">
                  ${Number(summary?.revenue?.total_revenue || analytics?.stats?.total_revenue || 0).toFixed(2)}
                </p>
                <span className="revenue-hint">All time earnings</span>
              </div>

              <div className="revenue-card">
                <span className="revenue-icon blue"><TrendingUp size={19} aria-hidden="true" /></span>
                <h3>Last 30 Days</h3>
                <p className="revenue-value">
                  ${Number(summary?.revenue?.revenue_30d || 0).toFixed(2)}
                </p>
                <span className="revenue-hint">{summary?.revenue?.total_sales || 0} sales</span>
              </div>

              <div className="revenue-card">
                <span className="revenue-icon teal"><Users size={19} aria-hidden="true" /></span>
                <h3>Total Students</h3>
                <p className="revenue-value">
                  {summary?.students?.total_students || analytics?.stats?.total_enrollments || 0}
                </p>
                <span className="revenue-hint">{summary?.students?.new_students_30d || 0} new this month</span>
              </div>

              <div className="revenue-card">
                <span className="revenue-icon amber"><Star size={19} aria-hidden="true" /></span>
                <h3>Average Rating</h3>
                <p className="revenue-value">
                  {Number(summary?.ratings?.avg_rating || 0).toFixed(1)}
                </p>
                <span className="revenue-hint">{summary?.ratings?.total_reviews || 0} reviews</span>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="card-heading table-heading">
                <span className="card-heading-icon green"><WalletCards size={17} aria-hidden="true" /></span>
                <h3>Revenue by course</h3>
              </div>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Enrollments</th>
                      <th>Completions</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.course_performance || []).map((course) => (
                      <tr key={course.id}>
                        <td>{course.title}</td>
                        <td>{course.enrollments}</td>
                        <td>{course.completions}</td>
                        <td className="revenue-cell">${parseFloat(course.revenue || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorDashboard;
